const WISHLIST_STORAGE_KEY = 'alphacraft:wishlist';
const WISHLIST_EVENT = 'wishlist:change';
const FALLBACK_IMAGE = 'https://cdn.shopify.com/s/files/1/0000/0000/files/placeholder.png?v=1';
const CART_ICON_MARKUP = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="9" cy="21" r="1"></circle><circle cx="20" cy="21" r="1"></circle><path d="M1 1h4l2.68 13.39A2 2 0 0 0 9.68 16h9.64a2 2 0 0 0 1.97-1.61L23 6H6"></path></svg>';

const WishlistUI = {
  showToast(message) {
    if (!message) return;
    const previous = document.querySelector('.wishlist-toast');
    if (previous) previous.remove();

    const toast = document.createElement('div');
    toast.className = 'wishlist-toast';
    toast.textContent = message;

    if (!document.getElementById('wishlist-toast-style')) {
      const style = document.createElement('style');
      style.id = 'wishlist-toast-style';
      style.textContent = `
        .wishlist-toast {
          position: fixed;
          bottom: 24px;
          left: 50%;
          transform: translateX(-50%);
          background: #232323;
          color: #fff;
          padding: 14px 24px;
          border-radius: 999px;
          font-size: 14px;
          font-weight: 600;
          z-index: 10000;
          box-shadow: 0 12px 30px rgba(0,0,0,0.25);
          animation: wishlist-toast-in 0.25s ease;
          max-width: min(90vw, 460px);
          min-width: 280px;
          text-align: center;
          line-height: 1.35;
        }

        @media screen and (max-width: 520px) {
          .wishlist-toast {
            width: calc(100% - 32px);
            min-width: auto;
            font-size: 13px;
            padding: 12px 16px;
            border-radius: 24px;
          }
        }

        @keyframes wishlist-toast-in {
          from { opacity: 0; transform: translateX(-50%) translateY(12px); }
          to { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
      `;
      document.head.appendChild(style);
    }

    document.body.appendChild(toast);
    setTimeout(() => {
      toast.style.opacity = '0';
      toast.style.transition = 'opacity 0.2s ease';
      setTimeout(() => toast.remove(), 200);
    }, 2500);
  }
};

class WishlistStore {
  constructor() {
    this.items = this.loadFromStorage();
    this.subscribers = new Set();
    this.productCache = new Map();
    this.currentLocale = window.theme.locale || 'bg';
    this.syncAcrossTabs();
    this.watchLocaleChange();
  }

  watchLocaleChange() {
    // Clear cache when locale changes (page navigation)
    const observer = new MutationObserver(() => {
      const newLocale = window.theme.locale || 'bg';
      if (newLocale !== this.currentLocale) {
        this.currentLocale = newLocale;
        this.productCache.clear();
        this.notify('locale-change');
      }
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['lang'] });
  }

  loadFromStorage() {
    try {
      const saved = localStorage.getItem(WISHLIST_STORAGE_KEY);
      if (!saved) return [];
      const parsed = JSON.parse(saved);
      if (Array.isArray(parsed)) {
        return parsed.filter(item => item && item.handle);
      }
      return [];
    } catch (error) {
      console.warn('Wishlist: unable to read localStorage', error);
      return [];
    }
  }

  saveToStorage() {
    try {
      localStorage.setItem(WISHLIST_STORAGE_KEY, JSON.stringify(this.items));
    } catch (error) {
      console.warn('Wishlist: unable to persist localStorage', error);
    }
  }

  syncAcrossTabs() {
    window.addEventListener('storage', (event) => {
      if (event.key !== WISHLIST_STORAGE_KEY) return;
      this.items = this.loadFromStorage();
      this.notify('sync');
    });
  }

  getSnapshot() {
    return {
      items: [...this.items],
      count: this.items.length
    };
  }

  subscribe(callback) {
    if (typeof callback !== 'function') return () => {};
    this.subscribers.add(callback);
    callback(this.getSnapshot(), { type: 'init' });
    return () => this.subscribers.delete(callback);
  }

  notify(type = 'update', payload = null) {
    const snapshot = this.getSnapshot();
    this.subscribers.forEach((callback) => {
      try {
        callback(snapshot, { type, payload });
      } catch (error) {
        console.error('Wishlist subscriber error', error);
      }
    });
    document.dispatchEvent(new CustomEvent(WISHLIST_EVENT, {
      detail: { snapshot, type, payload }
    }));
  }

  contains(handle) {
    return this.items.some((item) => item.handle === handle);
  }

  add(item) {
    if (!item || !item.handle) return;
    if (this.contains(item.handle)) return;

    this.items.unshift({
      handle: item.handle,
      id: item.id || null,
      title: item.title || '',
      image: item.image || null,
      price: typeof item.price === 'number' ? item.price : null,
      compareAtPrice: typeof item.compareAtPrice === 'number' ? item.compareAtPrice : null,
      rating: item.rating || null,
      ratingCount: item.ratingCount || null,
      variantId: item.variantId || null,
      addedAt: Date.now()
    });

    this.saveToStorage();
    this.notify('add', item);
  }

  remove(handle) {
    const nextItems = this.items.filter((item) => item.handle !== handle);
    if (nextItems.length === this.items.length) return;
    this.items = nextItems;
    this.saveToStorage();
    this.notify('remove', { handle });
  }

  toggle(item) {
    if (!item || !item.handle) return;
    if (this.contains(item.handle)) {
      this.remove(item.handle);
    } else {
      this.add(item);
    }
  }

  clear() {
    this.items = [];
    this.saveToStorage();
    this.notify('clear');
  }

  async fetchProduct(handle, storedItem = null) {
    if (!handle) return null;

    const cacheKey = `${handle}_${window.theme.locale}`;
    if (this.productCache.has(cacheKey)) {
      return this.productCache.get(cacheKey);
    }

    try {
      const locale = window.theme.locale || 'bg';
      const url = locale !== 'bg' ? `/${locale}/products/${handle}.js` : `/products/${handle}.js`;
      const response = await fetch(url);
      if (!response.ok) throw new Error('Network error');
      const product = await response.json();
      const normalized = this.normalizeProduct(product, storedItem);
      this.productCache.set(cacheKey, normalized);
      return normalized;
    } catch (error) {
      console.warn(`Wishlist: unable to fetch product ${handle}`, error);
      return null;
    }
  }

  async fetchAllProducts() {
    return Promise.all(this.items.map((item) => this.fetchProduct(item.handle, item)));
  }

  normalizeProduct(product, storedItem = null) {
    if (!product) return null;
    const price = typeof product.price === 'number' ? product.price : (product.variants?.[0]?.price ? Number(product.variants[0].price) * 100 : null);
    const compareAt = typeof product.compare_at_price === 'number'
      ? product.compare_at_price
      : (product.variants?.[0]?.compare_at_price ? Number(product.variants[0].compare_at_price) * 100 : null);

    const locale = window.theme.locale || 'bg';
    const productUrl = locale !== 'bg' ? `/${locale}/products/${product.handle}` : `/products/${product.handle}`;
    
    return {
      id: product.id,
      handle: product.handle,
      title: product.title,
      price,
      compareAtPrice: compareAt,
      priceFormatted: price != null ? this.formatMoney(price) : null,
      priceFormattedEUR: price != null ? this.formatMoneyEUR(price) : null,
      image: product.featured_image || product.images?.[0] || FALLBACK_IMAGE,
      url: productUrl,
      rating: product.metafields?.reviews?.rating_value || null,
      ratingCount: product.metafields?.reviews?.rating_count || null,
       variantId: product.variants?.length ? product.variants[0].id : null,
      vendor: product.vendor || '',
      raw: product
    };
  }

  formatMoney(amountInCents) {
    const amount = Number(amountInCents) / 100;
    const suffix = window.theme.translations.currency_suffix || ' лв';
    return `${amount.toFixed(2)}${suffix}`;
  }

  formatMoneyEUR(amountInCents) {
    const amountBGN = Number(amountInCents) / 100;
    const amountEUR = amountBGN / 1.95583;
    return `€${amountEUR.toFixed(2)}`;
  }
}

window.alphaWishlist = new WishlistStore();

class WishlistButtonController {
  constructor(store) {
    this.store = store;
    this.handleButtonClick = this.handleButtonClick.bind(this);
    this.unsubscribe = null;
    this.init();
  }

  init() {
    this.bindButtons(document);
    document.addEventListener('shopify:section:load', (event) => {
      this.bindButtons(event.target || document);
      this.refreshButtons(event.target || document);
    });

    this.unsubscribe = this.store.subscribe(() => {
      this.refreshButtons();
      this.updateBadges();
    });
  }

  bindButtons(root) {
    if (!root) return;
    const buttons = root.querySelectorAll('[data-wishlist-button]');
    buttons.forEach((button) => {
      if (button.dataset.wishlistBound === 'true') return;
      button.dataset.wishlistBound = 'true';
      button.addEventListener('click', this.handleButtonClick);
    });
  }

  handleButtonClick(event) {
    event.preventDefault();
    const button = event.currentTarget;
    const meta = this.extractMeta(button);
    if (!meta.handle) {
      console.warn('Wishlist button missing product handle');
      return;
    }
    button.classList.add('is-loading');
    this.store.toggle(meta);
    button.classList.remove('is-loading');
    this.showToast(meta);
  }

  extractMeta(button) {
    return {
      handle: button.dataset.productHandle,
      id: button.dataset.productId,
      title: button.dataset.productTitle,
      url: button.dataset.productUrl,
      price: Number(button.dataset.productPrice || '0'),
      compareAtPrice: button.dataset.productComparePrice ? Number(button.dataset.productComparePrice) : null,
      image: button.dataset.productImage || null,
      rating: button.dataset.productRating ? Number(button.dataset.productRating) : null,
      ratingCount: button.dataset.productRatingCount ? Number(button.dataset.productRatingCount) : null,
      variantId: button.dataset.variantId || null
    };
  }

  refreshButtons(root = document) {
    if (!root) return;
    const buttons = root.querySelectorAll('[data-wishlist-button]');
    buttons.forEach((button) => {
      const isSaved = this.store.contains(button.dataset.productHandle);
      button.classList.toggle('is-saved', isSaved);
      button.setAttribute('aria-pressed', isSaved ? 'true' : 'false');
      button.setAttribute('aria-label', isSaved ? window.theme.translations.wishlist_remove : window.theme.translations.wishlist_add);

      const addText = button.querySelector('[data-wishlist-text-add]');
      const removeText = button.querySelector('[data-wishlist-text-remove]');
      if (addText) addText.style.display = isSaved ? 'none' : 'inline';
      if (removeText) removeText.style.display = isSaved ? 'inline' : 'none';
    });
  }

  updateBadges() {
    const count = this.store.items.length;
    document.querySelectorAll('[data-wishlist-count]').forEach((el) => {
      el.textContent = count;
      el.hidden = count === 0;
    });
  }

  showToast(meta) {
    if (!meta || !meta.title) return;
    const added = this.store.contains(meta.handle);
    WishlistUI.showToast(added ? `"${meta.title}" ${window.theme.translations.wishlist_added}` : `"${meta.title}" ${window.theme.translations.wishlist_removed}`);
  }
}

class WishlistRenderer {
  constructor(store) {
    this.store = store;
    this.desktop = {
      list: document.querySelector('[data-wishlist-desktop-list]'),
      empty: document.querySelector('[data-wishlist-desktop-empty]')
    };
    const pageMobileList = document.querySelector('[data-wishlist-page-mobile]') || document.querySelector('[data-wishlist-page-list]');
    this.page = {
      list: pageMobileList,
      mobile: pageMobileList,
      desktop: document.querySelector('[data-wishlist-page-desktop]'),
      content: document.querySelector('[data-wishlist-page-content]'),
      empty: document.querySelector('[data-wishlist-page-empty]')
    };
    this.handleRemoveClick = this.handleRemoveClick.bind(this);
    this.handleAddToCart = this.handleAddToCart.bind(this);
    this.currentRenderToken = 0;

    this.store.subscribe((snapshot) => {
      this.render(snapshot);
    });
  }

  async render(snapshot) {
    const token = ++this.currentRenderToken;
    const items = snapshot.items;

    this.toggleEmptyState(items.length === 0);

    if (!items.length) {
      this.clearList(this.desktop.list);
      this.clearList(this.page.mobile);
      this.clearList(this.page.desktop);
      return;
    }

    const fallbackData = items.map((item) => this.mapStoredItem(item));
    this.paintLists(fallbackData);
    this.attachActionHandlers();

    try {
      const products = await this.store.fetchAllProducts();
      if (token !== this.currentRenderToken) return;
      const enriched = products.map((product, index) => product || fallbackData[index]);
      this.paintLists(enriched);
      this.attachActionHandlers();
    } catch (error) {
      console.warn('Wishlist: unable to fetch product details', error);
    }
  }

  toggleEmptyState(isEmpty) {
    if (this.desktop.empty) {
      this.desktop.empty.style.display = isEmpty ? 'block' : 'none';
    }
    if (this.desktop.list) {
      this.desktop.list.style.display = isEmpty ? 'none' : 'block';
    }
    if (this.page.empty) {
      this.page.empty.style.display = isEmpty ? 'block' : 'none';
    }
    if (this.page.content) {
      this.page.content.style.display = isEmpty ? 'none' : 'flex';
    } else if (this.page.list) {
      this.page.list.style.display = isEmpty ? 'none' : 'flex';
    }
  }

  paintLists(products) {
    if (this.desktop.list) {
      const desktopItems = products.slice(0, 4).map((product) => this.renderDesktopCard(product)).join('');
      this.desktop.list.innerHTML = desktopItems;
    }
    if (this.page.mobile) {
      this.page.mobile.innerHTML = products.map((product) => this.renderPageCard(product)).join('');
    }
    if (this.page.desktop) {
      this.page.desktop.innerHTML = products.map((product) => this.renderPageRow(product)).join('');
    }
  }

  clearList(list) {
    if (list) list.innerHTML = '';
  }

  mapStoredItem(item) {
    const locale = window.theme.locale || 'bg';
    const productUrl = locale !== 'bg' ? `/${locale}/products/${item.handle}` : `/products/${item.handle}`;
    
    return {
      handle: item.handle,
      title: item.title || '',
      url: productUrl,
      image: item.image || FALLBACK_IMAGE,
      priceFormatted: item.price != null ? this.store.formatMoney(item.price) : '',
      priceFormattedEUR: item.price != null ? this.store.formatMoneyEUR(item.price) : '',
      rating: item.rating || null,
      ratingCount: item.ratingCount || null,
      variantId: item.variantId || null
    };
  }

  renderDesktopCard(product) {
    if (!product) return '';
    const rating = product.rating ? `<div class="wishlist-dropdown__price">⭐ ${product.rating} ${product.ratingCount ? `(${product.ratingCount})` : ''}</div>` : '';
    return `
      <div class="wishlist-dropdown__item" data-handle="${product.handle}">
        <a href="${product.url}" class="wishlist-dropdown__media">
          <img src="${product.image}" alt="${product.title}">
        </a>
        <div>
          <a href="${product.url}" class="wishlist-dropdown__title">${product.title}</a>
          <div class="wishlist-dropdown__price">${product.priceFormatted || ''}</div>
          ${rating}
          <button type="button" class="wishlist-remove-btn" data-wishlist-remove="${product.handle}">${window.theme.translations.remove}</button>
        </div>
      </div>
    `;
  }

  renderPageCard(product) {
    if (!product) return '';
    const rating = product.rating ? `<div class="wishlist-page-card__rating">⭐ ${product.rating} ${product.ratingCount ? `(${product.ratingCount})` : ''}</div>` : '';
    const addDisabled = product.variantId ? '' : 'disabled';
    return `
      <div class="wishlist-page-card" data-handle="${product.handle}">
        <a href="${product.url}" class="wishlist-page-card__media">
          <img src="${product.image}" alt="${product.title}">
        </a>
        <div>
          <a href="${product.url}" class="wishlist-page-card__title">${product.title}</a>
          <div class="wishlist-page-card__prices">
            <span class="wishlist-price">${product.priceFormatted || ''}</span>
            <span class="wishlist-price wishlist-price--eur">${product.priceFormattedEUR || ''}</span>
          </div>
          ${rating}
          <div class="wishlist-page-card__actions">
            <button
              type="button"
              class="button button--primary wishlist-add-button"
              data-wishlist-add-to-cart
              data-variant-id="${product.variantId || ''}"
              ${addDisabled}
            >
              <span class="wishlist-button__icon" aria-hidden="true">${CART_ICON_MARKUP}</span>
              <span>${window.theme.translations.add_to_cart}</span>
            </button>
            <button type="button" class="wishlist-remove-btn" data-wishlist-remove="${product.handle}">
              ${window.theme.translations.remove}
            </button>
          </div>
        </div>
      </div>
    `;
  }

  renderPageRow(product) {
    if (!product) return '';
    const rating = product.rating ? `<span class="wishlist-table__rating">⭐ ${product.rating} ${product.ratingCount ? `(${product.ratingCount})` : ''}</span>` : '';
    const addDisabled = product.variantId ? '' : 'disabled';
    return `
      <div class="wishlist-table__row" data-handle="${product.handle}">
        <div class="wishlist-table__cell wishlist-table__cell--product">
          <a href="${product.url}" class="wishlist-table__thumb">
            <img src="${product.image}" alt="${product.title}">
          </a>
          <div class="wishlist-table__product-meta">
            <a href="${product.url}" class="wishlist-table__title">${product.title}</a>
            ${rating}
          </div>
        </div>
        <div class="wishlist-table__cell wishlist-table__cell--price">
          <span class="wishlist-price">${product.priceFormatted || ''}</span>
          <span class="wishlist-price wishlist-price--eur">${product.priceFormattedEUR || ''}</span>
        </div>
        <div class="wishlist-table__cell wishlist-table__cell--actions">
          <button
            type="button"
            class="button button--primary wishlist-add-button"
            data-wishlist-add-to-cart
            data-variant-id="${product.variantId || ''}"
            ${addDisabled}
          >
            <span class="wishlist-button__icon" aria-hidden="true">${CART_ICON_MARKUP}</span>
            <span>${window.theme.translations.add_to_cart}</span>
          </button>
          <button type="button" class="wishlist-remove-btn" data-wishlist-remove="${product.handle}">
            ${window.theme.translations.remove}
          </button>
        </div>
      </div>
    `;
  }

  attachActionHandlers() {
    const removeButtons = document.querySelectorAll('[data-wishlist-remove]');
    removeButtons.forEach((button) => {
      button.removeEventListener('click', this.handleRemoveClick);
      button.addEventListener('click', this.handleRemoveClick);
    });

    const addButtons = document.querySelectorAll('[data-wishlist-add-to-cart]');
    addButtons.forEach((button) => {
      button.removeEventListener('click', this.handleAddToCart);
      button.addEventListener('click', this.handleAddToCart);
    });
  }

  handleRemoveClick(event) {
    event.preventDefault();
    const handle = event.currentTarget.getAttribute('data-wishlist-remove');
    if (handle) {
      this.store.remove(handle);
    }
  }

  async handleAddToCart(event) {
    event.preventDefault();
    const button = event.currentTarget;
    const variantId = button.getAttribute('data-variant-id');
    if (!variantId) {
      WishlistUI.showToast(window.theme.translations.product_unavailable || 'Продуктът не може да се добави в момента.');
      return;
    }
    button.classList.add('is-loading');
    try {
      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 })
      });
      WishlistUI.showToast(window.theme.translations.added_to_cart || 'Добавено в количката');
    } catch (error) {
      console.error('Wishlist add-to-cart error', error);
      WishlistUI.showToast(window.theme.translations.error_adding || 'Грешка при добавяне');
    } finally {
      button.classList.remove('is-loading');
    }
  }
}

function initWishlistButtons() {
  if (!window.alphaWishlistButtons) {
    window.alphaWishlistButtons = new WishlistButtonController(window.alphaWishlist);
  } else {
    window.alphaWishlistButtons.bindButtons(document);
  }
}

function initWishlistRenderer() {
  if (!window.alphaWishlistRenderer) {
    window.alphaWishlistRenderer = new WishlistRenderer(window.alphaWishlist);
  }
}

function initWishlistFeatures() {
  initWishlistButtons();
  initWishlistRenderer();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initWishlistFeatures);
} else {
  initWishlistFeatures();
}
